import React, { useState, useEffect } from 'react'
import { Form, InputGroup, Row, Col, Button } from 'react-bootstrap'
import { getValidPreviousIntentId } from '../../../../utils/functions/getValidPreviousIntentId'
import { updateWhatsAppIntent } from '../../../../utils/functions/updateWhatsAppIntent'
import WhatsappWebhookComponent from './WhatsappWebhookComponent'
import useWebhookState from '../../../../utils/hooks/useWebhookState'
import {
  initializeTags,
  addTagToSession,
  removeTagFromSession,
  isTagAvailable
} from '../../../../utils/functions/tagUtils'
import { toast } from 'react-toastify'

interface IProps {
  intent: any
  inputJson: any[]
  setInputJson: React.Dispatch<React.SetStateAction<any[]>>
  closeModal: () => void
  data: any
}

interface Option {
  key: string
  label: string
}

const TextWithOptions: React.FC<IProps> = ({
  intent,
  inputJson,
  setInputJson,
  closeModal,
  data
}) => {
  const intentIndex = inputJson.findIndex((item) => item.id === intent.id)
  const allIntentIdsArray = inputJson.map((item) => item.id.toString())

  const [options, setOptions] = useState<Option[]>([])
  const [messageBody, setMessageBody] = useState(intent.text?.body || '')
  const [textareaRows, setTextareaRows] = useState(4)
  const { isWebhookChecked, handleWebhookCheckboxChange, handleFulfillmentChange } =
    useWebhookState(intent, intentIndex, inputJson, setInputJson, null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    initializeTags()
  }, [])

  const handleChange = (e: any) => {
    const name = e.target.name
    const value = e.target.value
    const newInputJson = [...inputJson]

    if (name === 'intentDescription') {
      newInputJson[intentIndex] = {
        ...intent,
        intentDescription: value
      }
      setInputJson(newInputJson)
    }

    if (name === 'tag') {
      const currentTag = intent?.tag || ''

      if (!isTagAvailable(value, currentTag)) {
        toast.error('This tag name is already in use. Please choose another name.')
        e.target.value = currentTag
        return
      }

      // Remove old tag from session if it exists
      if (currentTag && currentTag.trim() !== '') {
        removeTagFromSession(currentTag)
      }

      // Add new tag to session
      if (value && value.trim() !== '') {
        const tagAdded = addTagToSession(value)
        if (tagAdded) {
          newInputJson[intentIndex] = {
            ...intent,
            tag: value.trim()
          }
          setInputJson(newInputJson)
        }
      } else {
        newInputJson[intentIndex] = {
          ...intent,
          tag: value
        }
        setInputJson(newInputJson)
      }
    }
  }

  const calculateRows = (optCount: number) => {
    return Math.max(4, Math.min(12, 4 + optCount))
  }

  const updateInputJsonWithCurrentData = () => {
    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      text: {
        ...intent.text,
        body: messageBody
      }
    }
    setInputJson(newInputJson)
  }

  useEffect(() => {
    const parsedOptions = parseOptionsFromBody(intent.text?.body || '')
    setOptions(parsedOptions)
    setMessageBody(intent.text?.body || '')
    setTextareaRows(calculateRows(parsedOptions.length))
  }, [intent.id])

  useEffect(() => {
    updateInputJsonWithCurrentData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messageBody, options])

  const parseOptionsFromBody = (body: string): Option[] => {
    return body
      .split('\n')
      .filter((line: string) => /^\d+\./.test(line))
      .map((line: string) => {
        const [number, ...rest] = line.split('.')
        return {
          key: number.trim(),
          label: rest.join('.').trim()
        }
      })
  }

  const handleBodyChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = e.target.value
    setMessageBody(value)

    const parsedOptions = parseOptionsFromBody(value)
    setOptions(parsedOptions)

    setTextareaRows(calculateRows(parsedOptions.length))
  }

  const handleTriggerChange = (optionKey: string, newTrigger: string) => {
    const optionLabel = options.find((o) => o.key === optionKey)?.label || optionKey
    const newInputJson = [...inputJson]
    const newTriggerObj = { ...intent.trigger }

    // Remove old number-based key if exists
    if (newTriggerObj[optionKey]) delete newTriggerObj[optionKey]

    // Store numeric value, not string
    newTriggerObj[optionLabel] = newTrigger ? Number(newTrigger) : undefined

    newInputJson[intentIndex] = {
      ...intent,
      trigger: newTriggerObj
    }
    setInputJson(newInputJson)
  }

  const handleOptionLabelChange = (key: string, newLabel: string) => {
    const oldLabel = options.find((opt) => opt.key === key)?.label || key
    const newOptions = options.map((opt) => (opt.key === key ? { ...opt, label: newLabel } : opt))
    setOptions(newOptions)

    // Update message body
    const bodyLines = messageBody.split('\n')
    const updatedBodyLines = bodyLines.map((line: string) => {
      if (line.startsWith(`${key}.`)) {
        return `${key}.${newLabel}`
      }
      return line
    })
    setMessageBody(updatedBodyLines.join('\n'))

    // Update trigger keys (values remain numeric)
    if (intent.trigger && (intent.trigger[oldLabel] || intent.trigger[key])) {
      const newTrigger = { ...intent.trigger }
      const oldValue = newTrigger[oldLabel] || newTrigger[key]
      delete newTrigger[oldLabel]
      delete newTrigger[key]
      newTrigger[newLabel] = oldValue

      const newInputJson = [...inputJson]
      newInputJson[intentIndex] = {
        ...intent,
        trigger: newTrigger
      }
      setInputJson(newInputJson)
    }
  }

  const addOption = () => {
    if (options.length >= 10) return

    const nextKey =
      options.length > 0 ? String(Math.max(...options.map((o) => parseInt(o.key))) + 1) : '1'

    const newOption = { key: nextKey, label: 'New Option' }
    const newOptions = [...options, newOption]
    setOptions(newOptions)

    const newBody =
      messageBody +
      (messageBody && !messageBody.endsWith('\n') ? '\n' : '') +
      `${nextKey}.New Option`
    setMessageBody(newBody)

    const newInputJson = [...inputJson]
    newInputJson[intentIndex] = {
      ...intent,
      trigger: {
        ...intent.trigger,
        [newOption.label]: 1 // default numeric trigger value
      }
    }
    setInputJson(newInputJson)
    setTextareaRows(calculateRows(newOptions.length))
  }

  const removeOption = (key: string) => {
    const label = options.find((opt) => opt.key === key)?.label || key
    const newOptions = options.filter((opt) => opt.key !== key)
    setOptions(newOptions)

    const bodyLines = messageBody.split('\n')
    const updatedBodyLines = bodyLines.filter((line: string) => !line.startsWith(`${key}.`))
    setMessageBody(updatedBodyLines.join('\n'))

    if (intent.trigger && (intent.trigger[label] || intent.trigger[key])) {
      const newInputJson = [...inputJson]
      const newTrigger = { ...intent.trigger }
      delete newTrigger[label]
      delete newTrigger[key]

      newInputJson[intentIndex] = {
        ...intent,
        trigger: newTrigger
      }
      setInputJson(newInputJson)
    }

    setTextareaRows(calculateRows(newOptions.length))
  }

  const handleSave = async () => {
    const token = localStorage.getItem('auth-token')
    const whatsAppIDString = data?.whatsAppIDString
    const previousIntentId = getValidPreviousIntentId(
      intentIndex,
      inputJson,
      data?.WhatsAppInput || []
    )
    setIsSaving(true)
    await updateWhatsAppIntent({
      token,
      whatsAppIDString,
      intent,
      intentJson: inputJson[intentIndex],
      previousIntentId,
      onSuccess: closeModal
    })
  }

  const remainingChars = 1024 - (messageBody?.length || 0)

  return (
    <>
      <div className='p-3'>
        <h5 className='card-title h4 mb-5'>Text With Options</h5>

        <InputGroup className='my-3'>
          <InputGroup.Text>Intent Description</InputGroup.Text>
          <Form.Control
            id={`intentDescription-${intent.id}`}
            type='text'
            name='intentDescription'
            defaultValue={intent?.intentDescription}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>Tag</InputGroup.Text>
          <Form.Control
            id={`name-${intent.id}`}
            type='text'
            name='tag'
            defaultValue={intent?.tag}
            onBlur={handleChange}
          />
        </InputGroup>

        <InputGroup className='my-3'>
          <InputGroup.Text>Question Text</InputGroup.Text>
          <Form.Control
            as='textarea'
            rows={textareaRows}
            name='textBody'
            value={messageBody}
            onChange={handleBodyChange}
            maxLength={1024}
          />
        </InputGroup>

        <div className='d-flex justify-content-between align-items-center'>
          <h6 className='fw-bold mt-4 mb-3'>Options</h6>
          <div className='text-muted small'>{remainingChars} characters remaining</div>
        </div>

        {options.map(({ key, label }) => (
          <Row key={key} className='mb-3 align-items-center'>
            <Col xs={1} className='text-end pe-0'>
              <span className='fw-bold'>{key}.</span>
            </Col>
            <Col xs={6}>
              <Form.Control
                value={label}
                onChange={(e) => handleOptionLabelChange(key, e.target.value)}
              />
            </Col>
            <Col xs={3}>
              <Form.Select
                value={
                  intent.trigger?.[label] ||
                  intent.trigger?.[key] || // fallback for old format
                  ''
                }
                onChange={(e) => handleTriggerChange(key, e.target.value)}
              >
                {!(intent.trigger?.[label] || intent.trigger?.[key]) && (
                  <option value=''>Select Trigger</option>
                )}
                {allIntentIdsArray.map((id) => (
                  <option key={id} value={id}>
                    {id}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={1}>
              <Button variant='light-danger' size='sm' onClick={() => removeOption(key)}>
                <i className='bi bi-trash3'></i>
              </Button>
            </Col>
          </Row>
        ))}

        {options.length < 10 && (
          <Button
            variant='primary'
            className='mt-2 mb-4'
            onClick={addOption}
            disabled={options.length >= 10}
          >
            Add Option ({options.length}/10)
          </Button>
        )}
      </div>

      <div className='d-flex'>
        <label className='me-3 fs-5 fw-bolder mt-3'>
          Webhook
          <div className='form-check form-switch mt-3'>
            <input
              className='form-check-input'
              type='checkbox'
              id='flexSwitchCheckDefault'
              role='switch'
              name='webhookComponents'
              checked={isWebhookChecked}
              onChange={handleWebhookCheckboxChange}
            />
          </div>
        </label>
      </div>
      {/* )} */}

      {isWebhookChecked && (
        <WhatsappWebhookComponent
          inputJson={inputJson}
          intent={intent}
          handleFulfillmentChange={handleFulfillmentChange}
        />
      )}
      <div className='d-flex ms-auto mt-auto'>
        <button className='btn btn-secondary me-4' onClick={closeModal}>
          <i className='bi bi-eye-fill' /> Preview
        </button>
        <button className='btn btn-primary' onClick={handleSave} disabled={isSaving}>
          <i className='bi bi-save' /> {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </>
  )
}

export default TextWithOptions
